import { normalizeWorkspace } from '@/lib/selected-workspace.js'

// Sends and replies per Chicago day for the home screen: GET /api/business-campaigns/delivery/daily?from=&to=
// (backend/src/delivery/daily-report.mjs). Built like the business campaign client: the same bearer token checks, one
// bounded request (timeout, abort and a size cap), no credentials in the URL, and a response used whole or not at all.
export class SendsClientError extends Error {
  constructor(message, code, status = 0) {
    super(message)
    this.code = code
    this.status = status
  }
}

const fail = (message, code = 'INVALID_RESPONSE', status = 0) => { throw new SendsClientError(message, code, status) }
const object = x => x !== null && typeof x === 'object' && !Array.isArray(x)
const exact = (x, keys) => object(x) && Object.keys(x).length === keys.length && keys.every(k => Object.hasOwn(x, k))
const id = x => typeof x === 'string' && /^[A-Za-z0-9_-]{1,120}$/.test(x)
const count = x => Number.isSafeInteger(x) && x >= 0
const DAY = /^\d{4}-\d{2}-\d{2}$/
const realDay = d => typeof d === 'string' && DAY.test(d) && (t => Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === d)(Date.parse(`${d}T00:00:00Z`))
const countMap = m => object(m) && Object.entries(m).every(([k, n]) => id(k) && count(n))
const code = x => typeof x === 'string' && /^[A-Z_]{1,100}$/.test(x)

export const ZONE = 'America/Chicago'
export const WINDOW_DAYS = 30
const MAX_BYTES = 4 * 1024 * 1024

const CHICAGO = new Intl.DateTimeFormat('en-US', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' })
const chicagoDay = ms => Object.fromEntries(CHICAGO.formatToParts(ms).map(p => [p.type, p.value]))

// The last 30 Chicago days, today included, as the route's from and to.
export function lastDays(ms, days = WINDOW_DAYS) {
  const p = chicagoDay(ms)
  const to = `${p.year}-${p.month}-${p.day}`
  const from = new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day) - (days - 1))).toISOString().slice(0, 10)
  return { from, to }
}

// The answer, checked against what was asked. Anything off is an invalid response, never a partial series.
export function validDaily(v, range, workspace = null) {
  if (!exact(v, ['schemaVersion', 'workspaceId', 'timeZone', 'range', 'generatedAt', 'days', 'held'])) return false
  if (v.schemaVersion !== 1 || v.timeZone !== ZONE || !(v.workspaceId === null || id(v.workspaceId)) || !Number.isSafeInteger(v.generatedAt) || v.generatedAt <= 0) return false
  if (workspace !== null && v.workspaceId !== null && v.workspaceId !== workspace) return false
  if (!exact(v.range, ['from', 'to']) || v.range.from !== range.from || v.range.to !== range.to) return false
  if (!Array.isArray(v.days) || v.days.length > 90) return false
  let last = ''
  for (const d of v.days) {
    if (!exact(d, ['date', 'sent', 'replies', 'unknown']) || !realDay(d.date) || d.date <= last || d.date < range.from || d.date > range.to) return false
    if (!countMap(d.sent) || !countMap(d.replies) || !countMap(d.unknown)) return false
    last = d.date
  }
  return object(v.held) && Object.entries(v.held).every(([d, n]) => realDay(d) && d >= range.from && d <= range.to && count(n))
}

const MESSAGES = {
  401: 'Sign in again to view sends and replies.',
  403: 'This account is not allowed to view sends and replies.',
  429: 'Too many requests. Wait a moment and try again.',
}

export function createSendsClient({ baseUrl, getToken, expectedUserId, expectedSessionId, fetchImpl = fetch, allowLocal = false, timeoutMs = 15000, workspaceId = null, now = Date.now } = {}) {
  let workspace
  try { workspace = normalizeWorkspace(workspaceId) } catch { fail('The Engine sends service configuration is invalid.', 'INVALID_CONFIG') }
  let url
  try { url = new URL(baseUrl) } catch { fail('The Engine sends service is not configured.', 'INVALID_CONFIG') }
  const local = allowLocal && url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)
  const sessionId = x => typeof x === 'string' && /^[A-Za-z0-9_-]{1,120}$/.test(x)
  if ((url.protocol !== 'https:' && !local) || url.username || url.password || url.pathname !== '/' || url.search || url.hash ||
    ![expectedUserId, expectedSessionId].every(sessionId) || typeof getToken !== 'function' || typeof fetchImpl !== 'function' || typeof now !== 'function' ||
    !Number.isSafeInteger(timeoutMs) || timeoutMs <= 0 || timeoutMs > 60000) fail('The Engine sends service configuration is invalid.', 'INVALID_CONFIG')

  const tokenMatches = token => {
    try {
      const parts = token.split('.')
      if (parts.length !== 3 || parts.some(p => !p) || !/^[A-Za-z0-9_-]{1,16384}$/.test(parts[1])) return false
      const b64 = parts[1].replaceAll('-', '+').replaceAll('_', '/')
      const raw = atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='))
      const claims = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(raw, c => c.charCodeAt(0))))
      return claims?.sub === expectedUserId && claims?.sid === expectedSessionId
    } catch { return false }
  }

  async function request(path, { signal } = {}) {
    if (signal?.aborted) throw new DOMException('Request cancelled.', 'AbortError')
    const controller = new AbortController()
    let stopWith
    const stop = new Promise((_, reject) => { stopWith = reject })
    stop.catch(() => {})
    const halt = error => { stopWith(error); controller.abort() }
    const cancel = () => halt(new DOMException('Request cancelled.', 'AbortError'))
    signal?.addEventListener('abort', cancel, { once: true })
    const timer = setTimeout(() => halt(new SendsClientError('The sends service took too long to answer.', 'REQUEST_TIMEOUT')), timeoutMs)
    const bounded = promise => Promise.race([promise, stop])
    try {
      let token
      try { token = await bounded(Promise.resolve().then(() => getToken())) } catch (e) {
        if (e instanceof SendsClientError || e?.name === 'AbortError') throw e
        fail('Sign in again to view sends and replies.', 'AUTHENTICATION_REQUIRED', 401)
      }
      if (typeof token !== 'string' || !tokenMatches(token)) fail('Your sign-in changed. Reload the page.', 'AUTH_SESSION_CHANGED', 401)
      let response
      try {
        response = await bounded(fetchImpl(url.origin + path, {
          method: 'GET', signal: controller.signal, credentials: 'omit', cache: 'no-store', redirect: 'error',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        }))
      } catch (e) {
        if (e instanceof SendsClientError || e?.name === 'AbortError') throw e
        fail('The sends service could not be reached.', 'NETWORK_UNAVAILABLE')
      }
      let data = null
      try {
        const raw = await bounded(response.text())
        if (raw.length > MAX_BYTES) fail('The sends service returned an invalid response.')
        data = raw ? JSON.parse(raw) : null
      } catch (e) {
        if (e instanceof SendsClientError || e?.name === 'AbortError') throw e
        if (response.ok) fail('The sends service returned an invalid response.')
      }
      if (!response.ok) fail(MESSAGES[response.status] || 'The sends request failed.', object(data) && code(data.error) ? data.error : 'SENDS_REQUEST_FAILED', response.status)
      return data
    } finally {
      clearTimeout(timer)
      signal?.removeEventListener('abort', cancel)
    }
  }

  return Object.freeze({
    async series({ signal } = {}) {
      const range = lastDays(now())
      const query = new URLSearchParams()
      if (workspace) query.set('workspace', workspace)
      query.set('from', range.from)
      query.set('to', range.to)
      const value = await request(`/api/business-campaigns/delivery/daily?${query}`, { signal })
      if (!validDaily(value, range, workspace)) fail('The sends service returned an invalid answer.')
      return { range: value.range, days: value.days, held: value.held, workspaceId: value.workspaceId }
    },
  })
}
